/**
 * Actor-centric availability for a user action.
 *
 * Use this for action gates: "can this actor perform this action right now?" Prefer checking actor
 * permission before domain eligibility when constructing an action availability. Shared workflow
 * readiness/status should use a separate domain-specific view model instead.
 *
 * If permission-over-domain precedence becomes widespread, consider adding builder helpers that make
 * the precedence explicit instead of relying only on call-site ordering.
 */
export type ActionAvailability<Reason extends string> =
  | { available: true; reason: null }
  | { available: false; reason: Reason };

export namespace ActionAvailability {
  export const MISSING_PERMISSION = "missing_permission";
  export type MissingPermissionReason = typeof MISSING_PERMISSION;

  export function available(): ActionAvailability<never> {
    return { available: true, reason: null };
  }

  export function unavailable<Reason extends string>(reason: Reason): ActionAvailability<Reason> {
    return { available: false, reason };
  }

  export function isAvailable<Reason extends string>(
    availability: ActionAvailability<Reason>,
  ): availability is { available: true; reason: null } {
    return availability.available;
  }

  export function firstUnavailable<Reason extends string>(
    ...availabilities: readonly ActionAvailability<Reason>[]
  ): ActionAvailability<Reason> {
    return availabilities.find((availability) => !availability.available) ?? available();
  }
}
